export interface Material {
  id: number;
  name: string;
  description: string;
  rarity: string;
  iconName: string;
  imageUrl?: string;
  materialType: string;
  baseValue: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Recipe {
  id: number;
  name: string;
  description: string;
  category: string;
  requiredLevel: number;
  craftingTime: number;
  baseSuccessRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  resultEquipmentSlot?: string | null;
  /** Battlefield paper-doll overlay sprite (Stone Soup player layer path). */
  equipSpritePath?: string | null;
  craftedItem?: {
    id: number;
    name: string;
    itemType: string;
    rarity: string;
    iconName: string;
    imageUrl?: string;
  };
  ingredients: Array<{
    materialId: number;
    materialName: string;
    quantity: number;
    rarity: string;
  }>;
}

/** Mirrors server EquipmentSlot enum (Models/EquipmentSlot.cs); legacy Weapon/Armor/Accessory omitted. */
export const EQUIPMENT_SLOTS = [
  'MainHand', 'OffHand', 'Head', 'Chest', 'Legs', 'Feet', 'Hands',
  'Necklace', 'Ring1', 'Ring2', 'Belt', 'Back',
] as const;
export type EquipmentSlotName = typeof EQUIPMENT_SLOTS[number];

/** The 8 slots with a battlefield paper-doll overlay; Necklace/Ring1/Ring2/Belt have none. */
export const VISIBLE_EQUIPMENT_SLOTS: EquipmentSlotName[] = [
  'MainHand', 'OffHand', 'Head', 'Chest', 'Legs', 'Feet', 'Hands', 'Back',
];

/** Maps a visible equipment slot to its Stone Soup player sprite manifest category. */
export const SLOT_TO_SPRITE_CATEGORY: Record<string, string> = {
  MainHand: 'hand_right',
  OffHand: 'hand_left',
  Head: 'head',
  Chest: 'body',
  Legs: 'legs',
  Feet: 'boots',
  Hands: 'gloves',
  Back: 'cloak',
};

export interface Monster {
  id: number;
  name: string;
  description: string;
  level: number;
  health: number;
  damage: number;
  defense: number;
  speed: number;
  aiPersonality: string;
  monsterType: string;
  imageUrl?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  dropTable: Array<{
    materialId: number;
    materialName: string;
    dropRate: number;
    minQuantity: number;
    maxQuantity: number;
  }>;
}

export interface CreateMaterialData {
  name: string;
  description: string;
  rarity: string;
  iconName: string;
  materialType: string;
  baseValue: number;
  isActive: boolean;
}

export interface CreateRecipeData {
  name: string;
  description: string;
  category: string;
  requiredLevel: number;
  craftingTime: number;
  baseSuccessRate: number;
  craftedItemId: number;
  isActive: boolean;
  ingredients: Array<{
    materialId: number;
    quantity: number;
  }>;
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  requiresLoS?: boolean;
  weaponAPCost?: number;
  accuracyModifier?: number;
  usesAmmo?: boolean;
  maxAmmo?: number;
  equipmentSlot?: string | null;
  /** Battlefield paper-doll overlay sprite (Stone Soup player layer path), admin-picked. */
  equipSpritePath?: string | null;
}

export interface CreateMonsterData {
  name: string;
  description: string;
  level: number;
  health: number;
  damage: number;
  defense: number;
  speed: number;
  aiPersonality: string;
  monsterType: string;
  imageUrl?: string | null;
  isActive: boolean;
}

export interface MonsterSpriteManifestEntry {
  name: string;
  category: string;
  url: string;
}

export type ContentTab = 'materials' | 'recipes' | 'monsters';
export type AnyContent = Material | Recipe | Monster;
